import { formatoFechaBolivia } from '@/lib/utils/formato-precio';
import { describirReglaCompleta } from '@/features/pricing-rules/lib/reglas-en-palabras';
import { metodoEnLlano } from '../components/ficha-estrategia';
import { permisoEnPalabras } from '../components/permiso-de-estrategia';
import { TITULO_DE_LA_VIA } from './historial-de-precios';
import type { DetalleDeLaPropuesta, PriceProposalRow } from '../types/price-proposals.types';

/**
 * Lo que dice la izquierda de la fila abierta de Por decidir (lote B1, spec 246
 * punto 2; maqueta `b1-fila`), en palabras. Puro: el componente sólo pinta.
 * Las fechas, en hora de Bolivia y con el año (D10).
 */

const fecha = (iso: string) => formatoFechaBolivia(iso);
const minuscula = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);

/** «Apuntar a un margen · 27 % · siempre te pregunta». */
export function estrategiaEnPalabras(e: NonNullable<DetalleDeLaPropuesta['estrategia']>): string {
  return `${metodoEnLlano(e)} · ${minuscula(permisoEnPalabras(e.mode, e.askAboveChangePct))}`;
}

/**
 * Por qué una estrategia por filtro lo alcanza, con los datos del producto:
 * «la marca Ufesa», «la categoría Televisores», «la categoría X y la marca Y».
 * Otro filtro (precio, margen, stock): «su filtro».
 */
function porQueLoAlcanza(
  filtro: Record<string, unknown> | null,
  f: Pick<PriceProposalRow, 'category' | 'brand'>
): string {
  const tiene = (k: string) => Array.isArray(filtro?.[k]) && (filtro![k] as unknown[]).length > 0;
  const partes = [
    ...(tiene('categories') && f.category ? [`la categoría ${f.category}`] : []),
    ...(tiene('brands') && f.brand ? [`la marca ${f.brand}`] : []),
  ];
  return partes.length > 0 ? partes.join(' y ') : 'su filtro';
}

/** «Desde el 31 ago 2026, cuando lo elegiste a mano al crearla» y sus variantes. */
export function desdeCuandoEnPalabras(
  d: Pick<DetalleDeLaPropuesta, 'estrategia' | 'pertenencia'>,
  f: Pick<PriceProposalRow, 'category' | 'brand' | 'origenDelPedido'>
): string {
  const e = d.estrategia;
  const p = d.pertenencia;
  // Ronda del QA de R4: la del recálculo de un producto sin estrategia nunca la
  // tuvo: no es «ya no existe».
  if (!e && f.origenDelPedido === 'HOY')
    return 'El producto no está en ninguna estrategia: partió de su precio de hoy.';
  if (!e) return 'No se sabe: la estrategia que la propuso ya no existe.';
  if (!p)
    return `Todavía no se guardó: se guarda desde la próxima corrida de la estrategia (creada el ${fecha(e.createdAt)}).`;
  if (p.leftAt)
    return `Estuvo desde el ${fecha(p.enteredAt)} hasta el ${fecha(p.leftAt)}: hoy ya no es de esta estrategia.`;
  if (p.tomadoDe)
    return `Desde el ${fecha(p.tomadoEn ?? p.enteredAt)}, cuando se lo quitó a «${p.tomadoDe}»`;
  const aMano = e.grupo.kind === 'MANUAL';
  const porQue = porQueLoAlcanza(e.grupo.filter, f);
  const desde = fecha(p.enteredAt);
  switch (p.entry) {
    case 'AL_CREARLA':
      return aMano
        ? `Desde el ${desde}, cuando lo elegiste a mano al crearla`
        : `Desde el ${desde}: entró al crearla porque es de ${porQue}`;
    case 'ENTRO':
      return aMano
        ? `Desde el ${desde}, cuando lo sumaron a mano`
        : `Desde el ${desde}: entró solo porque es de ${porQue}`;
    default:
      return aMano
        ? `Ya estaba cuando se empezó a guardar, el ${desde} (lo elegiste a mano; la estrategia es del ${fecha(e.createdAt)})`
        : `Ya estaba cuando se empezó a guardar, el ${desde}: lo toma porque es de ${porQue} (la estrategia es del ${fecha(e.createdAt)})`;
  }
}

/**
 * «Carga de datos por archivo · «precios-maxiking-sept.xlsx» · Roberto Méndez».
 * Lote C2b (diferido de B1): en modo soporte, también quién actuó de verdad.
 * El nombre de la vía sale de la misma tabla que el historial del producto:
 * una sola lista para las dos pantallas (revisión conjunta).
 */
export function comoCambioEnPalabras(u: NonNullable<DetalleDeLaPropuesta['ultimoCambio']>): string {
  const titulo =
    u.via === 'ESTRATEGIA' && u.sola ? TITULO_DE_LA_VIA.AUTOMATICA : TITULO_DE_LA_VIA[u.via];
  const partes: string[] = [];
  // Un deshacer (D4) guarda «Deshacer un cambio» como origen: como en el historial,
  // se dice qué cambio deshizo (ronda del 5-oct).
  const deshizo = u.via === 'VOLVER' && u.deshizoEl != null;
  if (deshizo) partes.push(`deshizo el cambio del ${formatoFechaBolivia(u.deshizoEl!)}`);
  else if (u.fuente) partes.push(u.via === 'API' ? `clave «${u.fuente}»` : `«${u.fuente}»`);
  if (u.quien) partes.push(u.quien);
  const conQuien = partes.length > 0 ? `${titulo} · ${partes.join(' · ')}` : titulo;
  return u.soporte ? `${conQuien} · lo hizo ${u.soporte}, del soporte de Prixia` : conQuien;
}

/** «Margen Minimo Global» (no ganar menos de 18 %) · «Redondeo Psicológico» (…). null si ninguna. */
export function reglasEnPalabras(reglas: DetalleDeLaPropuesta['reglas']): string | null {
  if (reglas.length === 0) return null;
  return reglas
    .map((r) =>
      !r.existe
        ? `«${r.nombre}» (ya no existe)`
        : r.actions && r.actions.length > 0
          ? `«${r.nombre}» (${minuscula(describirReglaCompleta(r.actions))})`
          : `«${r.nombre}»`
    )
    .join(' · ');
}
